import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import SignUpModal from './SignUpModal';
import EmailVerificationModal from './EmailVerificationModal';

const { FiCamera, FiMapPin, FiSettings, FiUsers, FiStar, FiCheck, FiMail } = FiIcons;

const LandingPage = () => {
  const [showLogin, setShowLogin] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [email, setEmail] = useState('');
  const [loginStatus, setLoginStatus] = useState(null);
  const { sendMagicLink, validateMagicLink, showEmailVerification, setShowEmailVerification, pendingUser } = useAuth();
  const navigate = useNavigate();

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    setLoginStatus({ type: 'loading', message: 'Sende Login-Link...' });

    try {
      const result = await sendMagicLink(email);
      
      if (result.success) {
        setLoginStatus({ 
          type: 'success', 
          message: 'Login-Link wurde gesendet! Bitte prüfen Sie Ihre E-Mail oder warten Sie einen Moment für Demo-Accounts.' 
        });
      } else {
        setLoginStatus({ type: 'error', message: result.error || 'Fehler beim Senden des Login-Links' });
      }
    } catch (error) {
      console.error('Magic link request error:', error);
      setLoginStatus({ type: 'error', message: 'Ein unerwarteter Fehler ist aufgetreten' });
    }
  };

  const features = [
    {
      icon: FiCamera,
      title: 'Selfie-App für Städte',
      description: 'Ihre Besucher können Selfies mit Ihrem Stadtbranding erstellen'
    },
    {
      icon: FiMapPin,
      title: 'GPS-Integration',
      description: 'Automatische Standorterkennung für authentische Selfies'
    },
    {
      icon: FiSettings,
      title: 'Vollständig anpassbar',
      description: 'Farben, Logos, Texte - alles nach Ihren Wünschen'
    },
    {
      icon: FiUsers,
      title: 'Einfache Nutzung',
      description: 'QR-Code scannen und sofort loslegen - keine App-Installation nötig'
    }
  ];

  // Updated plans - only 2 tiers
  const plans = [
    {
      id: 'professional',
      name: 'Professional',
      price: 129,
      period: 'pro Monat',
      features: [
        'Unbegrenzte Selfies',
        'Vollständige Anpassung',
        'Analytics Dashboard',
        'QR-Code Generator',
        'Custom Domains',
        'White-Label Option',
        'API Zugang',
        'Multi-Location Support',
        'Email Support'
      ],
      popular: true
    },
    {
      id: 'concierge',
      name: 'Concierge',
      price: 229,
      period: 'pro Monat',
      features: [
        'Alle Professional Features',
        '✨ All done for you Service',
        '🚀 Priority Support',
        'Dedicated Account Manager',
        'Setup-Service inklusive',
        'Custom Entwicklung',
        'Persönlicher Ansprechpartner',
        '24/7 Premium Support',
        'Concierge Service'
      ]
    }
  ];

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
    setShowSignUp(true);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              City<span className="text-yellow-400">Shot</span>
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Die B2B Selfie-Lösung für Städte und Tourismusregionen. 
              Lassen Sie Ihre Besucher unvergessliche Momente mit Ihrem Branding teilen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowSignUp(true)}
                className="bg-yellow-400 text-gray-900 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-yellow-300 transition-colors"
              >
                Kostenlos testen
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowLogin(true)}
                className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-gray-900 transition-colors"
              >
                Anmelden
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Warum CityShot?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Steigern Sie die Sichtbarkeit Ihrer Stadt in sozialen Medien und 
              schaffen Sie einzigartige Erlebnisse für Ihre Besucher.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center p-6 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 hover:shadow-lg transition-shadow"
              >
                <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={feature.icon} className="text-2xl text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Zwei perfekte Lösungen
            </h2>
            <p className="text-xl text-gray-600">
              Wählen Sie zwischen Self-Service oder Full-Service
            </p>
            <div className="mt-4 bg-green-100 text-green-800 px-4 py-2 rounded-full inline-block">
              💡 Tipp: Nutzen Sie Gutscheincode "TESTFREE" für 100% kostenlos!
            </div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className={`relative p-8 rounded-2xl ${
                  plan.popular
                    ? 'bg-blue-600 text-white shadow-2xl scale-105'
                    : 'bg-white text-gray-900 shadow-lg'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-yellow-400 text-gray-900 px-4 py-2 rounded-full text-sm font-semibold">
                      Beliebt
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="text-4xl font-bold mb-2">
                    {plan.price}€{' '}
                    <span className="text-lg font-normal opacity-70">
                      /{plan.period}
                    </span>
                  </div>
                  {plan.id === 'concierge' && (
                    <p className="text-sm opacity-80 mt-2">
                      ✨ Wir machen alles für Sie
                    </p>
                  )}
                </div>

                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <SafeIcon 
                        icon={FiCheck} 
                        className={`mr-3 ${
                          plan.popular ? 'text-yellow-400' : 'text-green-500'
                        }`} 
                      />
                      <span className={
                        (feature.includes('All done for you') || feature.includes('Priority Support')) 
                          ? 'font-semibold' 
                          : ''
                      }>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  className={`w-full py-3 px-6 rounded-xl font-semibold transition-colors ${
                    plan.popular
                      ? 'bg-yellow-400 text-gray-900 hover:bg-yellow-300'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {plan.id === 'concierge' ? 'Concierge buchen' : 'Jetzt starten'}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo Accounts Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Demo-Zugang testen
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Erleben Sie CityShot mit unseren Demo-Accounts und entdecken Sie alle Funktionen
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-xl"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiSettings} className="text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Super Admin</h3>
                  <p className="text-sm text-gray-600">Verwalten Sie alle Städte</p>
                </div>
              </div>
              <div className="bg-white p-3 rounded-lg mb-3">
                <p className="text-sm text-gray-800 font-mono">cityshot@clickbooster.app</p>
              </div>
              <button
                onClick={() => {
                  setEmail('cityshot@clickbooster.app');
                  setShowLogin(true);
                }}
                className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Als Super Admin einloggen
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiMapPin} className="text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Musterstadt</h3>
                  <p className="text-sm text-gray-600">Stadt-Dashboard testen</p>
                </div>
              </div>
              <div className="bg-white p-3 rounded-lg mb-3">
                <p className="text-sm text-gray-800 font-mono">musterstadt@clickbooster.app</p>
              </div>
              <button
                onClick={() => {
                  setEmail('musterstadt@clickbooster.app');
                  setShowLogin(true);
                }}
                className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Als Musterstadt einloggen
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Login Modal */}
      {showLogin && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-8 max-w-md w-full"
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              🔐 Passwortloser Login
            </h2>
            
            <div className="mb-6 p-4 bg-blue-50 rounded-xl">
              <div className="flex items-center gap-2 text-blue-700 mb-2">
                <SafeIcon icon={FiMail} />
                <span className="font-semibold">Sicherer Login per E-Mail</span>
              </div>
              <p className="text-blue-600 text-sm">
                Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen sicheren Login-Link.
              </p>
            </div>
            
            <form onSubmit={handleMagicLinkRequest} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  E-Mail Adresse
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="ihre-stadt@example.com"
                  required
                />
              </div>

              {loginStatus && (
                <div className={`p-3 rounded-lg text-sm ${
                  loginStatus.type === 'error'
                    ? 'bg-red-100 text-red-700'
                    : loginStatus.type === 'success'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {loginStatus.message}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={loginStatus?.type === 'loading'}
                  className={`flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 ${
                    loginStatus?.type === 'loading' ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
                >
                  <SafeIcon icon={FiMail} />
                  Login-Link senden
                  {loginStatus?.type === 'loading' && (
                    <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowLogin(false);
                    setLoginStatus(null);
                    setEmail('');
                  }}
                  className="flex-1 border border-gray-300 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}

      {/* Sign Up Modal */}
      <SignUpModal
        isOpen={showSignUp}
        onClose={() => setShowSignUp(false)}
        selectedPlan={selectedPlan}
      />

      {/* Email Verification Modal */}
      <EmailVerificationModal
        isOpen={showEmailVerification}
        onClose={() => setShowEmailVerification(false)}
        userEmail={pendingUser?.email}
        cityName={pendingUser?.cityName}
      />
    </div>
  );
};

export default LandingPage;